import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  listGemPackages,
  createGemCheckout,
  convertGems,
  formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import { Diamond, CreditCard, CurrencyDollar, ArrowsClockwise } from "@phosphor-icons/react";

// PayPal SVG (logo only — button is non-functional placeholder until creds are wired)
function PaypalIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M7.4 21.5h2.7l.6-3.9h2.4c3.7 0 6-1.9 6.6-5.4.3-1.7-.2-3-1.2-3.8-.4-.3-.9-.5-1.4-.7.1-.2.1-.4.2-.6.4-2.3-.8-3.6-3.7-3.6H8.2c-.4 0-.7.3-.8.7L5 19.8c0 .2 0 .4.1.5.1.1.3.2.5.2h1.8zm5.6-13.6c.9 0 1.6.2 2.1.6-.5 1.8-1.7 2.4-3.6 2.4H10l.7-3h2.3z"/>
    </svg>
  );
}

export default function BuyGemsDialog({
  open,
  onOpenChange,
  currentGems = 0,
  currentMoney = 0,
  onGemsChanged,
}) {
  const [packages, setPackages] = useState([]);
  const [rate, setRate] = useState(500);
  const [loadingPkg, setLoadingPkg] = useState(false);
  const [purchasing, setPurchasing] = useState(null); // package id being purchased
  const [convertAmount, setConvertAmount] = useState(100);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoadingPkg(true);
    listGemPackages()
      .then((d) => {
        if (!alive) return;
        setPackages(d.packages || []);
        setRate(d.conversion_rate || 500);
      })
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
      .finally(() => alive && setLoadingPkg(false));
    return () => {
      alive = false;
    };
  }, [open]);

  const handleStripeCheckout = useCallback(
    async (pkgId) => {
      setPurchasing(pkgId);
      try {
        const origin = window.location.origin;
        const { url } = await createGemCheckout(pkgId, origin);
        if (!url) throw new Error("No checkout URL returned");
        // Full-page redirect to Stripe-hosted checkout
        window.location.href = url;
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Could not start checkout");
        setPurchasing(null);
      }
    },
    []
  );

  const handleConvert = useCallback(async () => {
    const n = Math.floor(Number(convertAmount) || 0);
    if (n <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    if (n > currentGems) {
      toast.error("Not enough gems");
      return;
    }
    setConverting(true);
    try {
      const s = await convertGems(n);
      toast.success(`+$${(n * rate).toLocaleString()} cash`);
      onGemsChanged?.({ money: s.money, gems: s.gems });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setConverting(false);
    }
  }, [convertAmount, currentGems, rate, onGemsChanged]);

  const sortedPackages = useMemo(
    () => [...packages].sort((a, b) => a.amount - b.amount),
    [packages]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="buy-gems-dialog"
        className="rounded-none border border-cyan-400/30 bg-[#0A0E14] text-white max-w-3xl p-0"
      >
        <DialogHeader className="px-5 py-4 border-b border-white/10">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-cyan-300 flex items-center gap-2">
            <Diamond size={16} weight="fill" /> Gem Shop
          </DialogTitle>
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
            Balance:{" "}
            <span className="text-cyan-300" data-testid="gems-balance">
              {currentGems.toLocaleString()} gems
            </span>
            <span className="mx-2 text-zinc-700">·</span>
            <span className="text-emerald-400" data-testid="money-balance">
              ${currentMoney.toLocaleString()}
            </span>
          </div>
        </DialogHeader>

        <div className="px-5 py-4 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* ---------- Packages ---------- */}
          <section>
            <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-3">
              Purchase Gems
            </h3>
            {loadingPkg ? (
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">
                Loading packages…
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sortedPackages.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`gem-pkg-${p.id}`}
                    className="border border-cyan-400/20 bg-cyan-400/[0.03] p-4 flex flex-col"
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                        {p.label}
                      </span>
                      <span className="mono font-bold text-[15px] text-white">
                        ${p.amount.toFixed(2)}
                      </span>
                    </div>
                    <div className="my-3 flex items-center gap-2">
                      <Diamond size={22} weight="fill" className="text-cyan-300" />
                      <span className="text-2xl font-bold tracking-tight text-cyan-200">
                        {p.gems.toLocaleString()}
                      </span>
                      <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                        gems
                      </span>
                    </div>
                    <div className="mt-auto grid grid-cols-2 gap-2">
                      <Button
                        data-testid={`pkg-${p.id}-stripe-btn`}
                        disabled={purchasing === p.id}
                        onClick={() => handleStripeCheckout(p.id)}
                        className="rounded-none h-9 mono uppercase text-[10px] tracking-widest bg-cyan-400 text-black hover:bg-cyan-300"
                      >
                        <CreditCard size={13} className="mr-1.5" />
                        {purchasing === p.id ? "..." : "Card"}
                      </Button>
                      <Button
                        data-testid={`pkg-${p.id}-paypal-btn`}
                        disabled
                        title="PayPal coming soon — provide sandbox creds to enable"
                        className="rounded-none h-9 mono uppercase text-[10px] tracking-widest bg-transparent text-zinc-500 border border-white/10 hover:bg-white/5 disabled:opacity-60"
                      >
                        <PaypalIcon size={12} />
                        <span className="ml-1.5">PayPal</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="mono text-[10px] uppercase tracking-widest text-zinc-600 mt-3">
              Payments secured by Stripe. PayPal coming soon.
            </p>
          </section>

          {/* ---------- Convert gems -> in-game money ---------- */}
          <section className="border-t border-white/5 pt-5">
            <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-3 flex items-center gap-2">
              <ArrowsClockwise size={12} /> Convert Gems → Cash
            </h3>
            <div className="border border-white/10 bg-[#0F1115] p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
                Rate: 1 Gem = ${rate.toLocaleString()}
              </div>
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex flex-col">
                  <label className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
                    Gems to spend
                  </label>
                  <Input
                    data-testid="convert-amount-input"
                    type="number"
                    min={1}
                    max={currentGems}
                    value={convertAmount}
                    onChange={(e) => setConvertAmount(e.target.value)}
                    className="w-32 rounded-none bg-black border border-white/10 mono"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
                    You receive
                  </span>
                  <div className="mono text-emerald-400 font-bold text-lg" data-testid="convert-preview">
                    +${(Math.max(0, Math.floor(Number(convertAmount) || 0)) * rate).toLocaleString()}
                  </div>
                </div>
                <Button
                  data-testid="convert-btn"
                  disabled={converting || currentGems <= 0}
                  onClick={handleConvert}
                  className="rounded-none h-9 mono uppercase text-[10px] tracking-widest bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-50"
                >
                  <CurrencyDollar size={13} className="mr-1.5" />
                  {converting ? "Converting…" : "Convert"}
                </Button>
              </div>
              <div className="flex gap-2 mt-3">
                {[100, 500, 1000].map((n) => (
                  <button
                    key={n}
                    data-testid={`convert-preset-${n}`}
                    onClick={() => setConvertAmount(n)}
                    className="mono text-[10px] uppercase tracking-widest border border-white/10 px-2 py-1 hover:bg-white/5 text-zinc-400"
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* ---------- Premium items (placeholder) ---------- */}
        </div>
      </DialogContent>
    </Dialog>
  );
}
